import { IonIcon, IonPage, useIonRouter } from "@ionic/react";
import { useTranslation } from "react-i18next";
import { useForm } from "@tanstack/react-form";
import { usePostHog } from "posthog-js/react";
import { z } from "zod";
import { AuthContent } from "@/components/Auth/Content";
import { AuthHeader } from "@/components/Auth/Header";
import { AuthPageHeaderTitle } from "@/components/Auth/PageHeaderTitle";
import { AuthSubmitButton } from "@/components/Auth/SubmitButton";
import { FormInput } from "@/components/Form/Input";
import { getErrorText } from "@/components/Form/utils";
import { TitleIcon } from "@/components/TitleIcon";
import { backendService } from "@/lib/backend";
import { type EmailVerificationCodeDto } from "@/lib/backend/api";
import { getErrorMessages } from "@/lib/backend/utils/error";
import { useExtractedMutation } from "@/lib/backend/utils/hooks";
import { arrowPathIcon, envelopeIcon } from "@/lib/icons/@heroicons/react/24/outline";
import { Routes } from "@/lib/router";
import { Sentry } from "@/lib/sentry";
import { useAuthFlowStore } from "@/lib/store/auth-flow";
import { logger } from "@/logger";

export function AuthForgotPassword() {
	const posthog = usePostHog();
	const { t, i18n } = useTranslation();
	const state = useAuthFlowStore();
	const router = useIonRouter();

	const mutationSendCode = useExtractedMutation({
		mutationKey: ["auth", "password-reset", "send"],
		mutationFn: (variables: EmailVerificationCodeDto) =>
			backendService.auth.authControllerSendPasswordResetVerificationCode(variables, {
				format: "text",
			}),
	});

	const form = useForm({
		defaultValues: { email: state.email },
		validators: {
			onBlur: z.object({
				email: z.string().email(t("fields.email.errors.invalid")),
			}),
		},
		onSubmit: ({ value, formApi }) =>
			mutationSendCode.mutateAsync(value, {
				onSuccess: (_data, variables) => {
					posthog.capture("User Forgot Password Send", { success: true });
					useAuthFlowStore.setState(variables);
					router.push(Routes.AuthForgotPasswordVerify(), "forward");
				},
				onError: (error) => {
					Sentry.captureException(error);
					posthog.capture("User Forgot Password Send", { success: false });
					logger.getLogger("Auth").error("User Forgot Password Send", { error });

					formApi.setFieldMeta("email", (current) => ({
						...current,
						errorMap: {
							onSubmit: getErrorText({
								language: i18n.language,
								errors: getErrorMessages(error).map((message) => {
									switch (message) {
										case "Email exists!":
											return t("fields.email.errors.used");
										default:
											return message;
									}
								}),
							}),
						},
					}));
				},
			}),
	});

	return (
		<IonPage>
			<AuthHeader parts={{ backButton: { defaultHref: Routes.AuthSignIn() } }} />
			<AuthContent>
				<TitleIcon icon={arrowPathIcon} />

				<AuthPageHeaderTitle
					parts={{
						title: { children: t("page.auth.forgot-password.title") },
						subtitle: { children: t("page.auth.forgot-password.subtitle") },
					}}
				/>

				<form
					onSubmit={(event) => {
						event.preventDefault();
						event.stopPropagation();
						form.validateField("email", "blur");
						form.handleSubmit();
					}}
					className="flex flex-col gap-6"
				>
					<form.Field
						name="email"
						children={(field) => (
							<FormInput
								data-attr="page.auth.forgot-password.index.email"
								field={field}
								type="email"
								autofocusOnIonViewDidEnter
								label={t("fields.email.label")}
								autocomplete="email"
								inputMode="email"
								placeholder={t("fields.email.placeholder")}
							>
								<IonIcon slot="start" icon={envelopeIcon} aria-hidden="true" />
							</FormInput>
						)}
					/>

					<form.Subscribe
						selector={(state) => [state.canSubmit, state.isSubmitting]}
						children={([canSubmit, isSubmitting]) => (
							<AuthSubmitButton
								data-attr="page.auth.forgot-password.index.action.primary"
								disabled={!canSubmit || mutationSendCode.isSuccess}
								isSubmitting={isSubmitting}
							>
								{t("page.auth.forgot-password.action.primary")}
							</AuthSubmitButton>
						)}
					/>
				</form>
			</AuthContent>
		</IonPage>
	);
}
